/*
 * Назначение: вкладка «Менеджеры и уведомления» в настройках проекта.
 * Функционал: CRUD менеджеров (имя, Telegram, email, флаги default/active),
 * настройка канала уведомлений по умолчанию (TG chat_id, email).
 */

import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { managersApi } from "../../../lib/admin-api";
import { ApiError } from "../../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import type { ProjectDetails, ProjectManager } from "../../../lib/types";

import { useProjectMutation } from "./shared";

interface ManagerForm {
  name: string;
  telegram_username: string;
  telegram_chat_id: string;
  email: string;
  is_default: boolean;
  is_active: boolean;
}

const blankManager = (): ManagerForm => ({
  name: "",
  telegram_username: "",
  telegram_chat_id: "",
  email: "",
  is_default: false,
  is_active: true,
});

export function ManagersTab({ project }: { project: ProjectDetails }) {
  const queryClient = useQueryClient();
  const projectMutation = useProjectMutation("сохранить уведомления");

  const { data: managers = [], isLoading } = useQuery({
    queryKey: ["managers", project.slug],
    queryFn: () => managersApi.list(),
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ProjectManager | null>(null);
  const [form, setForm] = useState<ManagerForm>(blankManager());

  const [notifyForm, setNotifyForm] = useState({
    notify_telegram_chat_id: project.notify_telegram_chat_id ?? "",
    notify_email: project.notify_email ?? "",
  });
  useEffect(() => {
    setNotifyForm({
      notify_telegram_chat_id: project.notify_telegram_chat_id ?? "",
      notify_email: project.notify_email ?? "",
    });
  }, [project]);

  const startCreate = () => {
    setEditing(null);
    setForm(blankManager());
    setOpen(true);
  };

  const startEdit = (m: ProjectManager) => {
    setEditing(m);
    setForm({
      name: m.name,
      telegram_username: m.telegram_username ?? "",
      telegram_chat_id: m.telegram_chat_id ?? "",
      email: m.email ?? "",
      is_default: m.is_default,
      is_active: m.is_active,
    });
    setOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name.trim(),
        telegram_username: form.telegram_username.trim() || null,
        telegram_chat_id: form.telegram_chat_id.trim() || null,
        email: form.email.trim() || null,
        is_default: form.is_default,
        is_active: form.is_active,
      };
      return editing
        ? managersApi.update(editing.id, payload)
        : managersApi.create(payload);
    },
    onSuccess: () => {
      toast.success(editing ? "Менеджер обновлён" : "Менеджер добавлен");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["managers", project.slug] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.detail : "Не удалось сохранить"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => managersApi.remove(id),
    onSuccess: () => {
      toast.success("Менеджер удалён");
      queryClient.invalidateQueries({ queryKey: ["managers", project.slug] });
    },
    onError: () => toast.error("Не удалось удалить"),
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Менеджеры</CardTitle>
            <CardDescription>
              Кому уходят уведомления о заявках. Один менеджер может быть «по умолчанию».
            </CardDescription>
          </div>
          <Button onClick={startCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Добавить менеджера
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Загружаем…
            </div>
          )}
          {!isLoading && managers.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Менеджеров пока нет. Добавь хотя бы одного, чтобы начали приходить уведомления.
            </p>
          )}
          <div className="space-y-3">
            {managers.map((m) => (
              <div
                key={m.id}
                className="flex flex-col md:flex-row md:items-center justify-between gap-3 border rounded-lg p-3"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{m.name}</p>
                    {m.is_default && <Badge variant="secondary">по умолчанию</Badge>}
                    {!m.is_active && <Badge variant="outline">отключён</Badge>}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1 flex flex-wrap gap-3">
                    {m.telegram_username && <span>TG: {m.telegram_username}</span>}
                    {m.telegram_chat_id && <span>chat_id: {m.telegram_chat_id}</span>}
                    {m.email && <span>{m.email}</span>}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(m)}>
                    Изменить
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    disabled={deleteMutation.isPending}
                    onClick={() => {
                      if (confirm(`Удалить менеджера «${m.name}»?`)) {
                        deleteMutation.mutate(m.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Канал уведомлений по умолчанию</CardTitle>
          <CardDescription>
            Если ни у одного менеджера не выставлен «по умолчанию», уведомления уйдут сюда.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="n-tg">Telegram chat_id</Label>
              <Input
                id="n-tg"
                value={notifyForm.notify_telegram_chat_id}
                onChange={(e) =>
                  setNotifyForm((s) => ({
                    ...s,
                    notify_telegram_chat_id: e.target.value,
                  }))
                }
                placeholder="-100123456789 или 123456789"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="n-email">Email</Label>
              <Input
                id="n-email"
                type="email"
                value={notifyForm.notify_email}
                onChange={(e) =>
                  setNotifyForm((s) => ({ ...s, notify_email: e.target.value }))
                }
                placeholder="manager@example.com"
              />
            </div>
          </div>
          <Button
            onClick={() =>
              projectMutation.mutate({
                notify_telegram_chat_id: notifyForm.notify_telegram_chat_id || null,
                notify_email: notifyForm.notify_email || null,
              })
            }
            disabled={projectMutation.isPending}
          >
            {projectMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Сохранить
          </Button>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? `Менеджер: ${editing.name}` : "Новый менеджер"}
            </DialogTitle>
            <DialogDescription>
              Хотя бы одно поле для связи (Telegram username, chat_id или email)
              должно быть заполнено.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="m-name">Имя</Label>
              <Input
                id="m-name"
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="m-tg-user">Telegram username</Label>
                <Input
                  id="m-tg-user"
                  placeholder="@manager"
                  value={form.telegram_username}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, telegram_username: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="m-tg-id">Telegram chat_id</Label>
                <Input
                  id="m-tg-id"
                  value={form.telegram_chat_id}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, telegram_chat_id: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="m-email">Email</Label>
              <Input
                id="m-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
              />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  id="m-default"
                  checked={form.is_default}
                  onCheckedChange={(v) =>
                    setForm((s) => ({ ...s, is_default: Boolean(v) }))
                  }
                />
                <Label htmlFor="m-default">По умолчанию</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="m-active"
                  checked={form.is_active}
                  onCheckedChange={(v) =>
                    setForm((s) => ({ ...s, is_active: Boolean(v) }))
                  }
                />
                <Label htmlFor="m-active">Активен</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || !form.name.trim()}
            >
              {saveMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
